import { createRandomString, INoteRef, QuerySortType } from "@fokused-me/core";
import { ContentModeType, usePromise, useSingleton } from "@fokused-me/web-ui";
import { List, Set } from "immutable";
import * as React from "react";
import { IDesktopInfo, IEditPanelInfo, INotePanelInfo, IPanelInfo, ITreePanelInfo } from "./Model";
import { readSystemDesktop, readSystemPanels, writeSystemDesktop, writeSystemPanels } from "./systemStorage";

export const createPanelInfo = ({
   notepadId,
   rootNoteId,
   type,
}: {
   notepadId: string;
   rootNoteId: string;
   type: ContentModeType;
}) => {
   switch (type) {
      case "tree":
         return {
            id: createRandomString(10),
            type: "tree",
            expanded: true,
            notepadId,
            rootNoteId,
            expandedNote: Set(),
            expandedText: Set(),
         } as ITreePanelInfo;
      case "note":
         return {
            id: createRandomString(10),
            type: "note",
            expanded: true,
            notepadId,
            rootNoteId,
         } as INotePanelInfo;
      case "edit":
         return {
            id: createRandomString(10),
            type: "edit",
            expanded: true,
            notepadId,
            rootNoteId,
            patches: List(),
            effectivePatches: List(),
         } as IEditPanelInfo;
      case "bin":
         return {
            id: createRandomString(10),
            type: "note",
            expanded: true,
            notepadId,
            rootNoteId,
         } as INotePanelInfo;
   }
};

let panels = List<IPanelInfo>();
let setPanels: React.Dispatch<React.SetStateAction<List<IPanelInfo>>>;

let desktopInfo: IDesktopInfo = {
   clearOnDrop: true,
   copyOnDrop: false,
   selectedNotes: List(),
};
let setDesktopInfo: React.Dispatch<React.SetStateAction<IDesktopInfo>>;

function savePanels(panels: List<IPanelInfo>) {
   setPanels(panels);
   return writeSystemPanels(panels);
}

function saveDesktopInfo(desktop: IDesktopInfo) {
   setDesktopInfo(desktop);
   return writeSystemDesktop(desktop);
}

function addPanel(panel: IPanelInfo) {
   savePanels(panels.unshift(panel));
}

function toggleExpandedNote(panelId: string, noteId: string) {
   const panelIndex = panels.findIndex((it) => it.id === panelId)!;
   const panel = panels.get(panelIndex)!;

   if (panel.type === "tree") {
      const newPanel: ITreePanelInfo = {
         ...panel,
         expandedNote: panel.expandedNote.contains(noteId)
            ? panel.expandedNote.remove(noteId)
            : panel.expandedNote.add(noteId),
      };
      savePanels(panels.set(panelIndex, newPanel));
   }
}

function toggleExpandedText(panelId: string, noteId: string) {
   const panelIndex = panels.findIndex((it) => it.id === panelId)!;
   const panel = panels.get(panelIndex)!;

   if (panel.type === "tree") {
      const newPanel: ITreePanelInfo = {
         ...panel,
         expandedText: panel.expandedText.contains(noteId)
            ? panel.expandedText.remove(noteId)
            : panel.expandedText.add(noteId),
      };
      savePanels(panels.set(panelIndex, newPanel));
   }
}

function toggleExpandPanel(panelId: string) {
   const panelIndex = panels.findIndex((it) => it.id === panelId)!;
   const panel = panels.get(panelIndex)!;

   savePanels(panels.set(panelIndex, { ...panel, expanded: !panel.expanded }));
}

function toggleSortMode(panelId: string, sortMode: QuerySortType) {
   const panelIndex = panels.findIndex((it) => it.id === panelId)!;
   const panel = panels.get(panelIndex)!;

   if (panel.type === "tree") {
      const newSortMode = panel.sortMode !== sortMode ? sortMode : undefined;
      savePanels(panels.set(panelIndex, { ...panel, sortMode: newSortMode }));
   }
}

function toggleSearchEnabled(panelId: string) {
   const panelIndex = panels.findIndex((it) => it.id === panelId)!;
   const panel = panels.get(panelIndex)!;

   if (panel.type === "note" || panel.type === "tree") {
      const searchEnabled = !panel.searchEnabled;
      savePanels(
         panels.set(panelIndex, { ...panel, searchEnabled, searchText: searchEnabled ? panel.searchText : undefined })
      );
   }
}

function setSearchText(panelId: string, text?: string) {
   const panelIndex = panels.findIndex((it) => it.id === panelId)!;
   const panel = panels.get(panelIndex)!;

   if (panel.type === "note" || panel.type === "tree") {
      savePanels(panels.set(panelIndex, { ...panel, searchText: text }));
   }
}

function setEditPatches(panelId: string, patches: List<string>, effectivePatches: List<string>) {
   const panelIndex = panels.findIndex((it) => it.id === panelId)!;
   const panel = panels.get(panelIndex)!;
   switch (panel.type) {
      case "edit":
         savePanels(panels.set(panelIndex, { ...panel, patches, effectivePatches }));
   }
}

function setPaneRootNoteId(panelId: string, rootNoteId: string) {
   const panelIndex = panels.findIndex((it) => it.id === panelId)!;
   const panel = panels.get(panelIndex)!;
   savePanels(panels.set(panelIndex, { ...panel, rootNoteId }));
}

function closePanels(panelIds: List<string>) {
   const panelIndexes = panelIds
      .map((id) => panels.findIndex((it) => it.id === id))
      .sort()
      .reverse();
   let newPanels = panels;
   panelIndexes.forEach((idx) => {
      newPanels = newPanels.delete(idx);
   });
   savePanels(newPanels);
}

function setPanelType(panelId: string, type: ContentModeType) {
   const panelIndex = panels.findIndex((it) => it.id === panelId)!;
   const panel = panels.get(panelIndex)!;
   const newInfo = { ...panel, type } as IPanelInfo;
   switch (newInfo.type) {
      case "tree":
         newInfo.expandedNote = newInfo.expandedNote || Set();
         newInfo.expandedText = newInfo.expandedText || Set();
         break;
      case "edit":
         newInfo.patches = List();
         newInfo.effectivePatches = List();
         break;
   }

   // if (panel.type === "bin" && newInfo.type !== "bin") {
   //    newInfo.rootNoteId = SpecialNotes.HOME;
   // }

   // if (panel.type !== "bin" && newInfo.type === "bin") {
   //    newInfo.rootNoteId = SpecialNotes.BIN;
   // }

   savePanels(panels.set(panelIndex, newInfo));
}

function toggleSelectionCopyOnDrop() {
   const newDesktopInfo = { ...desktopInfo, copyOnDrop: !desktopInfo.copyOnDrop };
   saveDesktopInfo(newDesktopInfo);
}

function toggleSelectionClearOnDrop() {
   const newDesktopInfo = { ...desktopInfo, clearOnDrop: !desktopInfo.clearOnDrop };
   saveDesktopInfo(newDesktopInfo);
}

function toggleNoteSelection(noteRef: INoteRef) {
   let selectedNotes: List<INoteRef>;
   if (desktopInfo.selectedNotes.find((it) => it.noteId === noteRef.noteId)) {
      selectedNotes = desktopInfo.selectedNotes.filter((it) => it.noteId !== noteRef.noteId);
   } else {
      selectedNotes = desktopInfo.selectedNotes.push(noteRef);
   }
   const newDesktopInfo = { ...desktopInfo, selectedNotes };
   saveDesktopInfo(newDesktopInfo);
}

function clearNoteSelection() {
   const newDesktopInfo = { ...desktopInfo, selectedNotes: List() };
   saveDesktopInfo(newDesktopInfo);
}

/* eslint-disable-next-line react-hooks/rules-of-hooks */
const usePanelsSingleton = useSingleton(() => usePromise(readSystemPanels));

/* eslint-disable-next-line react-hooks/rules-of-hooks */
const useDesktopSingleton = useSingleton(() => usePromise(readSystemDesktop));

export function useDesktop() {
   const { result: panelsResult, setResult: setPanelsResult } = usePanelsSingleton();
   panels = panelsResult || panels;
   setPanels = (setPanelsResult as any) || setPanels;

   const { result: desktopInfoResult, setResult: setDesktopInfoResult } = useDesktopSingleton();
   desktopInfo = desktopInfoResult || desktopInfo;
   setDesktopInfo = (setDesktopInfoResult as any) || setDesktopInfo;

   return {
      panels,
      toggleExpandedNote,
      toggleExpandedText,
      toggleExpandPanel,
      setPaneRootNoteId,
      addPanel,
      closePanels,
      setPanelType,
      setEditPatches,
      desktopInfo,
      toggleSelectionCopyOnDrop,
      toggleSelectionClearOnDrop,
      toggleNoteSelection,
      clearNoteSelection,
      toggleSearchEnabled,
      setSearchText,
      toggleSortMode,
   };
}
